import debounce from 'lodash-es/debounce'
import Cookies from 'js-cookie'

import { setContentHeight, setCountdownBarHeight } from '@/js/utils/helpers'

import { useCart } from '@/js/composables/cart'
import { useFlyouts } from '@/js/composables/flyouts'

import Minicart from '@/js/components/minicart'
import HeaderSearch from '@/js/components/header-search'
import MegamenuRecommendations from '@/js/components/megamenu-recommendations'
import MegamenuLinks from '@/js/components/megamenu-links'

export default {
  components: {
    Minicart,
    HeaderSearch,
    MegamenuRecommendations,
    MegamenuLinks
  },

  setup () {
    const {
      cart,
      updateCartAttributes
    } = useCart()

    const {
      flyouts,
      megamenuToggles,
      toggleFlyout,
      initMegamenu,
      closeAllMegamenus
    } = useFlyouts()

    return {
      cart,
      updateCartAttributes,
      flyouts,
      megamenuToggles,
      toggleFlyout,
      initMegamenu,
      closeAllMegamenus
    }
  },

  directives: {
    initMegamenu: {
      inserted (el, binding, vnode) {
        vnode.context.initMegamenu(el, binding, vnode)
      }
    }
  },

  data: () => ({
    lastScrollTop: 0,
    delta: window.matchMedia('(min-width: 1280px)').matches ? 134 : 62,
    mainHeaderHeight: 0,
    defaultAddress: null,
    locationPreference: null,
    isPlaceSuggestOpen: false,
    isLocationPreferenceFocused: false,
    showLocationResetButton: false,
    placesSuggestions: [],
    url: null,
    locationViewMode: 'display',
    showLocationTooltip: false,
    locationConfirmed: false,
    tooltipAutoHideTimer: null,
    previousLocationValue: null
  }),

  watch: {
    locationPreference (value) {
      // Decode and check if location contains "undefined" (case insensitive)
      let decodedValue = value
      if (value) {
        try {
          decodedValue = decodeURIComponent(value)
        } catch (e) {
          // If decoding fails, use original value
          decodedValue = value
        }
      }

      // Switch to input mode if location becomes undefined/empty/invalid
      const isInvalid = !decodedValue ||
                       decodedValue === '' ||
                       decodedValue === 'undefined' ||
                       decodedValue === 'null' ||
                       /undefined/i.test(decodedValue)

      if (isInvalid) {
        this.locationViewMode = 'input'
        this.placesSuggestions = []
        this.locationPreference = ''
        return
      }

      const isInputFocused = this.$refs.placeSuggestionInput === document.activeElement ||
                             this.$refs.placeSuggestionInputMobile === document.activeElement

      if (value && isInputFocused) {
        this.performPlaceSearch()
      } else if (!isInputFocused) {
        this.placesSuggestions = []
      }
    }
  },

  computed: {
    headerClass () {
      return {
        'header-show': !this.lastScrollTop || this.lastScrollTop <= this.delta,
        'header-hide': this.lastScrollTop > this.delta
      }
    }
  },

  created () {
    this.url = new URL(window.location.href)

    if (Cookies.get('postal_code')) {
      this.url.searchParams.set('postCode', Cookies.get('postal_code'))
      window.history.replaceState({}, '', this.url)
    } else {
      this.geocodeCoordinates()
    }

    if (Cookies.get('location_preference')) {
      let locationPref = Cookies.get('location_preference')
      // Decode URL-encoded cookie value
      try {
        locationPref = decodeURIComponent(locationPref)
      } catch (e) {
        // If decoding fails, use original value
        console.warn('Failed to decode location_preference cookie:', e)
      }

      // Check if location contains "undefined" (case insensitive)
      const isInvalid = !locationPref ||
                       locationPref === 'undefined' ||
                       locationPref === 'null' ||
                       locationPref.trim() === '' ||
                       /undefined/i.test(locationPref)

      if (isInvalid) {
        // If location is invalid, clear cookie and show input mode
        Cookies.remove('location_preference')
        this.locationPreference = ''
        this.locationViewMode = 'input'
      } else {
        // Location is valid
        this.defaultAddress = locationPref
        this.locationPreference = locationPref
        this.locationViewMode = 'display'
        // Check tooltip eligibility after location is set
        this.$nextTick(() => {
          this.checkTooltipEligibility()
        })
      }
    } else {
      this.geocodeCoordinates()
      // If no location, start in input mode until location is set
      this.locationViewMode = 'input'
    }
  },

  mounted () {
    // Hide content until header has loaded
    document.querySelector('body').classList.remove('hidden')

    window.addEventListener('location-updated', this.handleLocationUpdated)

    setCountdownBarHeight()
    // Fix for iOS Safari 100vh issue
    setContentHeight()
    window.addEventListener('resize', () => setContentHeight())

    // Fixed header on scroll up
    this.mainHeaderHeight = this.$refs.mainHeaderElement.offsetHeight
    window.addEventListener('scroll', this.handleScroll)

    document.addEventListener('click', this.handleClickOutside)

    // Check tooltip eligibility after component is fully mounted
    // Use setTimeout to ensure DOM is ready and location is set
    // This is a fallback in case the check in created() didn't run or location wasn't ready
    setTimeout(() => {
      // In design mode, always check eligibility (will show if enabled)
      // Otherwise, only check if location exists
      const headerElement = this.$refs.mainHeaderElement
      const isDesignMode = headerElement?.dataset?.designMode === 'true' || window.Shopify?.designMode

      if (isDesignMode || (this.locationPreference && this.locationViewMode === 'display' && !this.showLocationTooltip)) {
        this.checkTooltipEligibility()
      }
    }, 500)
  },

  beforeUnmount () {
    document.removeEventListener('click', this.handleClickOutside)
  },

  beforeDestroy () {
    window.removeEventListener('location-updated', this.handleLocationUpdated)
    window.removeEventListener('scroll', this.handleScroll)
    // Clear tooltip auto-hide timer to prevent memory leaks
    if (this.tooltipAutoHideTimer) {
      clearTimeout(this.tooltipAutoHideTimer)
      this.tooltipAutoHideTimer = null
    }
  },

  methods: {
    handleMenuMask (show = false) {
      const body = document.querySelector('body')

      if (show) {
        body.classList.remove('overflow-hidden')
        if (!this.locationPreference) {
          setTimeout(() => {
            this.locationPreference = Cookies.get('location_preference')
          }, 1000)
        }
      } else {
        body.classList.add('overflow-hidden')
      }
    },

    handleLocationPreferenceFocus (event) {
      event.target.select()
      this.isLocationPreferenceFocused = true
      this.showLocationResetButton = true
      // Hide tooltip if visible when user focuses on input
      if (this.showLocationTooltip) {
        this.hideTooltip()
      }
      // Store the previous value to detect changes when typing
      this.previousLocationValue = this.locationPreference
    },

    handleLocationPreferenceInput () {
      // Ensure focused state is set when typing
      this.isLocationPreferenceFocused = true
      // Explicitly trigger search when user types
      if (this.locationPreference && this.locationPreference.trim()) {
        this.performPlaceSearch()
      } else {
        this.placesSuggestions = []
        this.isPlaceSuggestOpen = false
      }
    },

    handleResetLocationPreference () {
      this.isLocationPreferenceFocused = false
      this.locationPreference = ''
      this.showLocationResetButton = false
      // Keep in input mode when resetting
      this.locationViewMode = 'input'
    },

    handleClickOutside (event) {
      // Get the location preference containers (desktop and mobile)
      const locationContainer = document.querySelector('.location-preference')
      const locationContainerMobile = document.querySelector('.location-preference-mobile')

      const isInsideLocation = (locationContainer && locationContainer.contains(event.target)) ||
                              (locationContainerMobile && locationContainerMobile.contains(event.target))

      // Check if click is outside the location preference area
      if (!isInsideLocation) {
        // Only handle input mode state if in input mode
        if (this.locationViewMode === 'input') {
          this.isLocationPreferenceFocused = false
          this.showLocationResetButton = false
        }
        // Hide tooltip if clicking outside
        if (this.showLocationTooltip) {
          this.hideTooltip()
        }
      }
    },

    handleScroll () {
      const scrollTopPosition = document.documentElement.scrollTop
      const header = this.$refs.mainHeaderElement

      if (Math.abs(this.lastScrollTop - scrollTopPosition) <= this.delta) return

      header.classList.toggle('header-hide', scrollTopPosition > this.lastScrollTop && scrollTopPosition > this.mainHeaderHeight)

      // scroll down
      if (scrollTopPosition > this.lastScrollTop && scrollTopPosition > this.mainHeaderHeight) {
        // close minicart on scroll down
        if (this.flyouts.minicart) {
          this.toggleFlyout('minicart')
        }
      }

      header.classList.toggle('header-show', scrollTopPosition <= this.lastScrollTop || scrollTopPosition + window.innerHeight < document.documentElement.scrollHeight)

      this.lastScrollTop = scrollTopPosition
    },

    geocodeCoordinates () {
      fetch('https://ipapi.co/json/')
        .then(response => response.json())
        .then(data => {
          /* eslint-disable camelcase */
          const { city, postal, region_code } = data

          // Only set location if all values are valid (not undefined)
          if (city && postal && region_code && city !== 'undefined' && postal !== 'undefined' && region_code !== 'undefined') {
            this.locationPreference = `${city}, ${region_code} ${postal}`
            this.defaultAddress = `${city}, ${region_code} ${postal}`
            Cookies.set('location_preference', `${city}, ${region_code} ${postal}`, { expires: 365 })
            Cookies.set('postal_code', postal, { expires: 365 })
            this.url.searchParams.set('postCode', Cookies.get('postal_code'))
            window.history.replaceState({}, '', this.url)
            Cookies.set('state', region_code, { expires: 365 })
            this.updateCartAttributes('postal_code', postal)
            // Update view mode to display after successful location detection
            this.locationViewMode = 'display'
            if (this.$el) {
              setTimeout(() => {
                this.checkTooltipEligibility()
              }, 100)
            }
          } else {
            // If location is invalid, keep in input mode
            this.locationViewMode = 'input'
          }
          /*
          // Enable this if Google Geocode is required
          const geocoder = new google.maps.Geocoder()
          // latitude & longitude values will be coming from data (IP API response)
          const coordinates = {
            lat: parseFloat(latitude),
            lng: parseFloat(longitude)
          }
          geocoder
            .geocode({ location: coordinates })
            .then((response) => {
              if (response.results[0]) {
                // Handle geocode response
              }
            })
            .catch((error) => {
              console.error('Geocoder failed:', error)
            })
          */
          /* eslint-enable camelcase */
        })
        .catch(error => {
          console.error('Error fetching user location:', error)
        })
    },

    performPlaceSearch: debounce(async function () {
      // eslint-disable-next-line no-undef
      const placesService = new google.maps.places.AutocompleteService()
      const self = this

      if (this.locationPreference) {
        placesService.getPlacePredictions({
          input: this.locationPreference,
          types: ['administrative_area_level_1', 'locality', 'political', 'postal_code'],
          componentRestrictions: { country: 'AU' },
          fields: ['formatted_address', 'place_id']
        }, function (predictions, status) {
          // eslint-disable-next-line no-undef
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            self.placesSuggestions = predictions
            self.isPlaceSuggestOpen = true
          } else {
            console.error('Place suggestions request failed:', status)
          }
        })
      }
    }, 500),

    getDetailedAddress (id) {
      let formattedAddress = ''
      let updatedPostCode = ''
      const addressBody = {}
      const currentPostCode = Cookies.get('postal_code')
      // eslint-disable-next-line no-undef
      const placesService = new google.maps.places.PlacesService(document.createElement('div'))
      placesService.getDetails(
        {
          placeId: id,
          fields: ['address_components', 'formatted_address', 'name']
        },
        (place, status) => {
          // eslint-disable-next-line no-undef
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            const selectedAddress = place.address_components
            let stateCode = ''

            for (const component of selectedAddress) {
              if (component.types.includes('locality')) {
                addressBody.city = component.short_name
              }

              if (component.types.includes('administrative_area_level_1')) {
                addressBody.state = component.short_name
                stateCode = component.short_name
                Cookies.set('state', component.short_name, { expires: 365 })
              }

              if (component.types.includes('postal_code')) {
                addressBody.postCode = component.short_name
                updatedPostCode = component.short_name
                Cookies.set('postal_code', component.short_name, { expires: 365 })
                this.url.searchParams.set('postCode', Cookies.get('postal_code'))
                window.history.replaceState({}, '', this.url)
                this.updateCartAttributes('postal_code', component.short_name)
              }
            }

            if (addressBody.city && addressBody.state && addressBody.postCode) {
              formattedAddress = `${addressBody.city}, ${addressBody.state} ${addressBody.postCode}`
            } else {
              // when searching by suburb (administrative_level_1), some details for setting up postcode will be missing, this script fills out the missing details
              if (stateCode) {
                const fallbackPostCodes = {
                  NSW: '2000',
                  ACT: '2000',
                  VIC: '3000',
                  QLD: '4000',
                  SA: '5000',
                  WA: '6000',
                  TAS: '7000',
                  NT: '0800'
                }
                Cookies.set('postal_code', fallbackPostCodes[stateCode], { expires: 365 })
                this.url.searchParams.set('postCode', Cookies.get('postal_code'))
                window.history.replaceState({}, '', this.url)
                this.updateCartAttributes('postal_code', fallbackPostCodes[stateCode])
              }

              formattedAddress = place.formatted_address
            }

            this.placesSuggestions = []
            this.locationPreference = formattedAddress
            Cookies.set('location_preference', formattedAddress, { expires: 365 })

            if (currentPostCode.charAt(0) !== updatedPostCode?.charAt(0)) {
              location.reload()
            }

            this.$nextTick(() => {
              this.isPlaceSuggestOpen = false
              this.showLocationResetButton = false
              // Switch back to display mode after selection
              this.showLocationDisplay()
            })
          } else {
            console.error('PlacesService failed due to: ', status)
          }
        }
      )
    },

    showLocationInput () {
      this.locationViewMode = 'input'
      // Set focused state immediately
      this.isLocationPreferenceFocused = true
      this.showLocationResetButton = true
      // Hide tooltip if visible when switching to input mode
      if (this.showLocationTooltip) {
        this.hideTooltip()
      }
      this.$nextTick(() => {
        // Focus on desktop or mobile input depending on screen size
        const mobileInput = this.$refs.placeSuggestionInputMobile
        const desktopInput = this.$refs.placeSuggestionInput
        if (window.innerWidth < 1280 && mobileInput) {
          mobileInput.focus()
          mobileInput.select()
        } else if (desktopInput) {
          desktopInput.focus()
          desktopInput.select()
        }
      })
    },

    handleLocationUpdated (event) {
      if (event?.detail?.locationPreference) {
        this.locationPreference = event.detail.locationPreference
        this.locationViewMode = 'display'
      }
    },

    showLocationDisplay () {
      this.locationViewMode = 'display'
      this.isLocationPreferenceFocused = false
      this.showLocationResetButton = false
      this.placesSuggestions = []
    },

    checkTooltipEligibility () {
      // Check if tooltip is enabled
      const headerElement = this.$refs.mainHeaderElement
      const tooltipEnabled = headerElement?.dataset?.tooltipEnabled
      if (tooltipEnabled === 'false') {
        return false
      }

      // Always show in theme editor (design mode) when enabled
      const isDesignMode = headerElement?.dataset?.designMode === 'true' || window.Shopify?.designMode
      if (isDesignMode && tooltipEnabled !== 'false') {
        this.showLocationTooltip = true
        this.startTooltipAutoHide()
        return true
      }

      // Check if tooltip was already shown in this session
      if (sessionStorage.getItem('location_tooltip_shown') === 'true') {
        return false
      }

      // Get days between shows from settings
      const daysBetweenShows = parseInt(headerElement?.dataset?.tooltipDaysBetweenShows || '7')

      // Check if X days have passed since last shown
      const lastShownDate = localStorage.getItem('location_tooltip_last_shown')
      if (lastShownDate) {
        const daysSince = (Date.now() - parseInt(lastShownDate)) / (1000 * 60 * 60 * 24)
        if (daysSince < daysBetweenShows) {
          return false
        }
      }

      // Show tooltip if location exists
      if (this.locationPreference) {
        this.showLocationTooltip = true
        this.startTooltipAutoHide()
        return true
      }

      return false
    },

    handleTooltipConfirm () {
      this.locationConfirmed = true
      Cookies.set('location_tooltip_confirmed', 'true', { expires: 365 })
      localStorage.setItem('location_tooltip_last_shown', Date.now().toString())
      this.hideTooltip()
    },

    handleTooltipChange () {
      this.hideTooltip()
      this.showLocationInput()
    },

    hideTooltip () {
      this.showLocationTooltip = false
      sessionStorage.setItem('location_tooltip_shown', 'true')
      // Store last shown date for X-day check (even if auto-hidden)
      if (!localStorage.getItem('location_tooltip_last_shown')) {
        localStorage.setItem('location_tooltip_last_shown', Date.now().toString())
      }
      if (this.tooltipAutoHideTimer) {
        clearTimeout(this.tooltipAutoHideTimer)
        this.tooltipAutoHideTimer = null
      }
    },

    startTooltipAutoHide () {
      if (this.tooltipAutoHideTimer) {
        clearTimeout(this.tooltipAutoHideTimer)
      }

      // Get auto-hide delay from settings (default 10 seconds)
      const headerElement = this.$refs.mainHeaderElement
      const autoHideSeconds = parseInt(headerElement?.dataset?.tooltipAutoHideSeconds || '10')

      // If auto-hide is 0, don't set a timer (tooltip won't auto-close)
      if (autoHideSeconds === 0) {
        return
      }

      const autoHideMs = autoHideSeconds * 1000

      this.tooltipAutoHideTimer = setTimeout(() => {
        this.hideTooltip()
      }, autoHideMs)
    }
  }
}
